import React, { useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, ShieldCheck, Loader2, Download, AlertTriangle, CheckCircle2, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { lnsRotateSovrana, downloadJson, apiErr } from "@/lib/api";
import { frasiNuove, chiaviDaSeme } from "@/lib/seme";

export default function FirstAccessGate({ children }) {
  const { user, getPrivateKey, updateUser, setKeyfile, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [newKf, setNewKf] = useState(null);
  // Le 12 parole nascono QUI, su questo dispositivo. Nessuno le ha mai viste prima,
  // e nessuno le rivedra' dopo: non esistono altrove.
  const [frasi, setFrasi] = useState("");
  const [scritte, setScritte] = useState(false);

  if (!user?.must_rotate_key) return children;

  const fileName = `lattice-key-${user.lns?.replace(/[@.]/g, "_")}.json`;

  const rotate = async () => {
    setBusy(true); setErr("");
    try {
      const sk = getPrivateKey();
      if (!sk) throw new Error("Chiave attuale non trovata. Esci e rientra con il file chiave ricevuto.");
      // HANDOVER SOVRANO: prima il seme, poi la chiave. La chiave e' una CONSEGUENZA
      // delle 12 parole, quindi il dipendente puo' rientrare da un telefono nuovo anche
      // senza il file, e la chiave temporanea dell'azienda muore in questo istante.
      const f12 = frasiNuove().join(" ");
      const kp = chiaviDaSeme(f12, user.lns);
      const r = await lnsRotateSovrana(user.lns, sk, kp.publicKeyHex);
      const kf = { ...r.keyfile, seme: true, seme_frasi: f12, secret_key_hex: kp.secretKeyHex };
      setFrasi(f12);
      setKeyfile(kf);
      setNewKf(kf);
      downloadJson(kf, r.keyfile_filename || fileName);
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };

  const finish = () => { updateUser({ ...user, must_rotate_key: false }); };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col justify-center px-5 py-12" data-testid="first-access-gate">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-[#262626] bg-[#0d0d0d] p-8 lt-glow-primary">
        <div className="flex items-center gap-2 lp-c"><ShieldCheck className="h-5 w-5" /><span className="font-mono text-[11px] uppercase tracking-widest">Primo accesso · Sicurezza</span></div>
        <h1 className="mt-4 font-display text-3xl font-700">Rinnova la tua chiave</h1>
        <p className="mt-3 text-sm leading-relaxed text-[#a3a3a3]">
          Il tuo account è stato creato dalla tua azienda con una chiave temporanea. Per la tua sicurezza
          devi <span className="text-white">generare il tuo Master Seed di 12 parole</span>: la chiave nasce da quelle parole, su questo dispositivo, e da quel momento
          <span className="lp-c"> nessun altro</span> — nemmeno chi ha creato il tuo account — potrà più accedere.
        </p>

        {err && <div className="mt-5 flex items-center gap-2 border border-[#ff3333]/40 bg-[#ff3333]/10 p-3 text-sm text-[#ff9a9a]" data-testid="first-access-error"><AlertTriangle className="h-4 w-4" /> {err}</div>}

        {!newKf ? (
          <>
            <div className="mt-5 flex items-start gap-2 border border-[#ff9100]/30 bg-[#ff9100]/10 p-3 text-xs text-[#ffb958]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Riceverai <b>12 parole</b> e il file identità. Le parole sono la seconda chiave della stessa porta: con indirizzo e parole rientri anche da un telefono nuovo, senza il file. Non le rivedrai: scrivile su carta.</span>
            </div>
            <button onClick={rotate} disabled={busy} data-testid="rotate-key-btn" className="mt-6 flex w-full items-center justify-center gap-2 border lp-bd lp-b py-3 font-mono text-[12px] uppercase tracking-wider text-white hover:bg-[#3fa862] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4" /> Genera il mio Master Seed</>}
            </button>
          </>
        ) : (
          <>
            <div className="mt-5 flex items-center gap-2 border lp-bd40 lp-b10 p-3 text-sm lp-c" data-testid="rotate-success"><CheckCircle2 className="h-4 w-4" /> Master Seed generato. La chiave temporanea dell'azienda non è più valida: da adesso l'identità è solo tua.</div>
            <button onClick={() => downloadJson(newKf, fileName)} data-testid="rotate-download-again" className="mt-4 flex w-full items-center justify-center gap-2 border border-[#262626] py-2.5 font-mono text-[12px] uppercase tracking-wider text-[#a3a3a3] lp-h-bd hover:text-white">
              <Download className="h-4 w-4" /> Scarica di nuovo
            </button>
            <div className="mt-5 border lp-bd40 bg-[#0a0a0a] p-4" data-testid="first-access-frasi">
              <div className="font-mono text-[10px] uppercase tracking-widest lp-c">Le tue 12 parole master</div>
              <p className="mt-2 text-[11px] leading-relaxed text-[#8d9591]">
                Nessuno le ha mai viste, nemmeno noi: sono nate in questo browser un istante fa.
                Scrivile su carta e conservale offline. Non le rivedrai.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="first-access-frasi-elenco">
                {frasi.split(" ").filter(Boolean).map((w, i) => (
                  <div key={i} className="border border-[#1f1f1f] bg-[#050505] px-2 py-1.5 font-mono text-[12px] text-white">
                    <span className="mr-1.5 text-[9px] text-[#555]">{i + 1}</span>{w}
                  </div>
                ))}
              </div>
              <button onClick={() => setScritte(true)} data-testid="first-access-frasi-confermate"
                className={`mt-4 w-full border py-2.5 font-mono text-[11px] uppercase tracking-wider transition-colors ${scritte ? "lp-bd lp-b10 lp-c" : "border-[#333] text-[#8d9591] hover:border-[#3fa862]"}`}>
                {scritte ? "Dichiarato: le ho scritte" : "Ho scritto le 12 parole su carta"}
              </button>
            </div>
            <button onClick={finish} disabled={!scritte} data-testid="first-access-continue" className="mt-3 flex w-full items-center justify-center gap-2 border lp-bd lp-b10 py-3 font-mono text-[12px] uppercase tracking-wider lp-c lp-h-b20 disabled:opacity-40">
              Ho salvato seme e file · Continua
            </button>
          </>
        )}

        <button onClick={logout} className="mt-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#666] hover:text-white"><LogOut className="h-3.5 w-3.5" /> Esci</button>
      </motion.div>
    </div>
  );
}
